import { SelectProps } from 'antd';
import { useGetPublicRegionsQuery, useGetRegionsQuery } from 'gql/types';
import { Select } from 'modules/common/components';
import { useMemo } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type PropTypes = {
  controlled?: UseControllerProps<any>;
  value?: string;
  onRegionChange?: (newRegionId: string | null) => void;
  /**
   * Use the unauthenticated getPublicRegions query (id + name only).
   * Required on pages reachable without an internal-team login:
   * public /register-pet and the Owner-facing /donors/application.
   */
  publicRegionsOnly?: boolean;
} & SelectProps;

export default function RegionSelect({
  controlled,
  value: propValue,
  onRegionChange,
  publicRegionsOnly,
  ...selectProps
}: PropTypes) {
  const { t } = useTranslation('locations.RegionSelect');

  const regionsQuery = useGetRegionsQuery({ skip: publicRegionsOnly });
  const publicRegionsQuery = useGetPublicRegionsQuery({
    skip: !publicRegionsOnly,
  });

  const loading = publicRegionsOnly
    ? publicRegionsQuery.loading
    : regionsQuery.loading;

  const options = useMemo(() => {
    const regions = publicRegionsOnly
      ? publicRegionsQuery.data?.getPublicRegions
      : regionsQuery.data?.getRegions;
    return regions?.map((region) => ({
      label: region.name,
      value: region.id,
    }));
  }, [publicRegionsOnly, publicRegionsQuery.data, regionsQuery.data]);

  if (controlled) {
    return (
      <Controller
        {...controlled}
        render={({ field }) => (
          <Select
            {...selectProps}
            {...field}
            loading={loading}
            placeholder={t('selectRegion')}
            options={options}
            onChange={(value) => {
              const previousValue = field.value;
              field.onChange(value);

              if (previousValue !== value) {
                onRegionChange?.(value ?? null);
              }
            }}
          />
        )}
      />
    );
  }

  return (
    <Select
      loading={loading}
      placeholder={t('selectRegion')}
      options={options}
      value={propValue || undefined}
      onChange={(value) => {
        onRegionChange?.(value ?? null);
        selectProps.onChange?.(value, { label: '', value });
      }}
      {...selectProps}
    />
  );
}
